import { inject, Service } from '@angular/core';
import { Observable } from 'rxjs';

import { AppHttp, RequestOptions } from '@core/http/http.service';
import {
  OrgCancelBookingRequestDto,
  OrgConsentDecisionRequestDto,
  OrgCounselingBookingRequestDto,
  OrgGuardianDecisionRequestDto,
  OrgHealthSyncRequestDto,
  OrgProgressRequestDto,
  OrgPulseRequestDto,
  OrgWheelCheckinRequestDto,
} from './dtos/requests';
import {
  OrgBookingResultDto,
  OrgChildDigestEnvelopeDto,
  OrgChildrenDto,
  OrgConsentDecisionDto,
  OrgConsentsDto,
  OrgContextDto,
  OrgCounselingOptionsDto,
  OrgCounselingSessionsDto,
  OrgEngagementDto,
  OrgEngagementOverviewDto,
  OrgGuardianDecisionResultDto,
  OrgGuardianRequestsDto,
  OrgHealthCatalogDto,
  OrgHealthSyncResultDto,
  OrgProgressResultDto,
  OrgPulseDto,
  OrgWheelDto,
} from './dtos/responses';

/**
 * OrganizationsApiClient — thin transport for every member-facing B2B endpoint,
 * over {@link AppHttp} (which attaches the Sanctum bearer + `Accept-Language` +
 * `Accept: application/json`, and unwraps the `{status, message, data}` envelope
 * so each method below returns `data` directly).
 *
 * ── Why EVERY call is `{ silent: true }` ────────────────────────────────────
 * `AppHttp` auto-toasts `ApiError.message` for any non-silent call. For B2B that
 * would be actively wrong: §1.3 documents that `customResponse()` OVERWRITES the
 * message on every 403 with a generic access sentence, whatever the controller
 * passed. Toasting it would tell a member "you do not have the necessary access
 * rights" when the real reason was `not_yours` or `not_joined`.
 *
 * So the transport stays quiet and `OrgErrorMapper` decides what is shown — it
 * reads `data.reason`, prefers the server's own message ONLY on the statuses
 * §1.3 says preserve it (401/404/409/422), and falls back to our localized copy
 * everywhere else. The error is still normalised and rejected as an `ApiError`,
 * so nothing is swallowed.
 *
 * Auth: every endpoint here requires the bearer, so none is `anonymous` — with
 * one honest exception noted on {@link getWheel}, which the server answers for a
 * non-member too but still authenticates.
 */
@Service()
export class OrganizationsApiClient {
  private readonly http = inject(AppHttp);

  /**
   * Shared per-call options. `silent` for the reason above; kept as one constant
   * so a future call cannot forget it and start toasting a clobbered 403.
   */
  private static readonly OPTS: RequestOptions = { silent: true };

  // ── §2 context ──────────────────────────────────────────────────────────
  /**
   * `GET /api/v3/org/context` — the only call that decides whether the B2B home
   * is drawn. Never 404s and never 403s for a signed-in user; a non-member is a
   * 200 with `data.member: null` (trap 1).
   */
  getContext(): Observable<OrgContextDto> {
    return this.http.get<OrgContextDto>('/api/v3/org/context', OrganizationsApiClient.OPTS);
  }

  // ── §3 the wheel ────────────────────────────────────────────────────────
  /**
   * `GET /api/v3/org/wheel`. Answers for a NON-MEMBER too (the shared catalogue
   * with no org customisation), so it needs no context check first.
   */
  getWheel(): Observable<OrgWheelDto> {
    return this.http.get<OrgWheelDto>('/api/v3/org/wheel', OrganizationsApiClient.OPTS);
  }

  /**
   * `POST /api/v3/org/wheel/check-in` — returns THE ENTIRE WHEEL, identical in
   * shape to {@link getWheel}, so the caller re-renders from this response rather
   * than following up with a GET.
   */
  submitCheckin(body: OrgWheelCheckinRequestDto): Observable<OrgWheelDto> {
    return this.http.post<OrgWheelDto>('/api/v3/org/wheel/check-in', body, OrganizationsApiClient.OPTS);
  }

  // ── §4 engagement ───────────────────────────────────────────────────────
  /** `GET /api/v3/org/engagement` — challenges, streak, badges and pulse in one read. */
  getEngagement(): Observable<OrgEngagementDto> {
    return this.http.get<OrgEngagementDto>('/api/v3/org/engagement', OrganizationsApiClient.OPTS);
  }

  /**
   * `POST /api/v3/org/engagement/challenges/{id}/join` — NO BODY. Joining twice
   * is not an error; it returns the existing participation. The success payload
   * is the PARTIAL overview shape, not the full engagement one (trap 8).
   */
  joinChallenge(challengeId: number): Observable<OrgEngagementOverviewDto> {
    return this.http.post<OrgEngagementOverviewDto>(
      `/api/v3/org/engagement/challenges/${challengeId}/join`,
      {},
      OrganizationsApiClient.OPTS,
    );
  }

  /** `POST /api/v3/org/engagement/challenges/{id}/progress` — self-reported challenges only. */
  reportProgress(challengeId: number, body: OrgProgressRequestDto): Observable<OrgProgressResultDto> {
    return this.http.post<OrgProgressResultDto>(
      `/api/v3/org/engagement/challenges/${challengeId}/progress`,
      body,
      OrganizationsApiClient.OPTS,
    );
  }

  /** `POST /api/v3/org/engagement/pulse` — returns the PULSE BLOCK ONLY (trap 8). */
  answerPulse(body: OrgPulseRequestDto): Observable<OrgPulseDto> {
    return this.http.post<OrgPulseDto>('/api/v3/org/engagement/pulse', body, OrganizationsApiClient.OPTS);
  }

  // ── §5 health samples (outside the /org prefix on purpose) ──────────────
  /** `GET /api/v3/health/metrics` — the member's own data, org membership irrelevant. */
  getHealthCatalog(): Observable<OrgHealthCatalogDto> {
    return this.http.get<OrgHealthCatalogDto>('/api/v3/health/metrics', OrganizationsApiClient.OPTS);
  }

  /** `POST /api/v3/health/samples` — partial acceptance; read `accepted`/`rejected` (trap 11). */
  syncHealthSamples(body: OrgHealthSyncRequestDto): Observable<OrgHealthSyncResultDto> {
    return this.http.post<OrgHealthSyncResultDto>('/api/v3/health/samples', body, OrganizationsApiClient.OPTS);
  }

  // ── §7 school counseling ────────────────────────────────────────────────
  /** `GET /api/v3/org/counseling/options` — two shapes; branch on `available`/`reason` (trap 12). */
  getCounselingOptions(): Observable<OrgCounselingOptionsDto> {
    return this.http.get<OrgCounselingOptionsDto>('/api/v3/org/counseling/options', OrganizationsApiClient.OPTS);
  }

  /** `POST /api/v3/org/counseling/bookings` — `{ slot_id, date }`. */
  bookCounseling(body: OrgCounselingBookingRequestDto): Observable<OrgBookingResultDto> {
    return this.http.post<OrgBookingResultDto>('/api/v3/org/counseling/bookings', body, OrganizationsApiClient.OPTS);
  }

  /** `GET /api/v3/org/counseling/bookings` — the member's own, newest first, capped at 20. */
  getCounselingSessions(): Observable<OrgCounselingSessionsDto> {
    return this.http.get<OrgCounselingSessionsDto>('/api/v3/org/counseling/bookings', OrganizationsApiClient.OPTS);
  }

  /**
   * `POST /api/v3/org/counseling/bookings/{id}/cancel`. The body is optional —
   * an empty object is sent when there is no reason, so the key is absent rather
   * than an explicit null.
   */
  cancelCounseling(sessionId: number, body: OrgCancelBookingRequestDto): Observable<OrgCounselingSessionsDto> {
    return this.http.post<OrgCounselingSessionsDto>(
      `/api/v3/org/counseling/bookings/${sessionId}/cancel`,
      body,
      OrganizationsApiClient.OPTS,
    );
  }

  // ── §8 guardian ─────────────────────────────────────────────────────────
  /** `GET /api/v3/org/children` — `{list: []}` with a 200 for a non-guardian; never 403. */
  getChildren(): Observable<OrgChildrenDto> {
    return this.http.get<OrgChildrenDto>('/api/v3/org/children', OrganizationsApiClient.OPTS);
  }

  /**
   * `GET /api/v3/org/children/{member_id}/digest` — the path id is an ORG MEMBER
   * id from `/org/children`, NOT a user id (trap 14). Every read is audited.
   */
  getChildDigest(memberId: number): Observable<OrgChildDigestEnvelopeDto> {
    return this.http.get<OrgChildDigestEnvelopeDto>(
      `/api/v3/org/children/${memberId}/digest`,
      OrganizationsApiClient.OPTS,
    );
  }

  /** `GET /api/v3/org/consents` — referrals waiting on this guardian's decision. */
  getConsents(): Observable<OrgConsentsDto> {
    return this.http.get<OrgConsentsDto>('/api/v3/org/consents', OrganizationsApiClient.OPTS);
  }

  /** `POST /api/v3/org/consents/{referral_id}` — `{ granted, note? }`. */
  decideConsent(referralId: number, body: OrgConsentDecisionRequestDto): Observable<OrgConsentDecisionDto> {
    return this.http.post<OrgConsentDecisionDto>(
      `/api/v3/org/consents/${referralId}`,
      body,
      OrganizationsApiClient.OPTS,
    );
  }

  /** `GET /api/v3/org/counseling/guardian-requests` — §7.3 sessions plus the child's name. */
  getGuardianRequests(): Observable<OrgGuardianRequestsDto> {
    return this.http.get<OrgGuardianRequestsDto>(
      '/api/v3/org/counseling/guardian-requests',
      OrganizationsApiClient.OPTS,
    );
  }

  /** `POST /api/v3/org/counseling/guardian-requests/{session_id}` — `{ approve }`. */
  decideGuardianRequest(
    sessionId: number,
    body: OrgGuardianDecisionRequestDto,
  ): Observable<OrgGuardianDecisionResultDto> {
    return this.http.post<OrgGuardianDecisionResultDto>(
      `/api/v3/org/counseling/guardian-requests/${sessionId}`,
      body,
      OrganizationsApiClient.OPTS,
    );
  }
}
